import {
  CopyButton,
  ItemRow,
  PageSection,
  PageWrapper,
  PriceRow,
} from '@src/components';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { useGetQRCodeDataQuery } from '@src/services/esim/esimApiSlice';
import {
  useLazyGetOrderQuery,
  useLazyGetPdfQuery,
} from '@src/services/order/orderApiSlice';
import { ORDER_STATUS } from '@src/services/order/orderApiSliceTypes';
import { Button, Divider } from 'antd';
import dayjs from 'dayjs';
import { ArrowDown3, ArrowRight3, Bill } from 'iconsax-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from '../MyESims/components/QRCode';
import { PackageCard } from '../Package';
import { gatewayIcon } from './Billing';

interface PurchaseDetailProps {
  orderId: string;
  setFull?: (open: boolean) => void;
}

export const PurchaseDetailsModal: React.FC<
  Pick<PurchaseDetailProps, 'orderId'> & { onClose: () => void }
> = ({ orderId, onClose }) => {
  const [full, setFull] = useState(false);

  const [getInvoice] = useLazyGetPdfQuery();

  const handleDownloadInvoice = async () => {
    try {
      const result = await getInvoice({ id: orderId }).unwrap();

      if (result?.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to download invoice', error);
    }
  };

  return (
    <ResponsiveModal
      title='Purchase details'
      headerIcon={
        <Bill className='text-gray-600 fill-gray-300 bg-wl-surface' />
      }
      className={full ? 'max-md:max-h-[100vh] max-md:h-full' : ''}
      isOpen={true}
      onClose={onClose}
      content={<PurchaseDetails setFull={setFull} orderId={orderId} />}
      footer={
        <PageSection>
          <Button
            className='btn-secondary w-full h-[48px] px-10 py-2 rounded-lg'
            onClick={handleDownloadInvoice}
          >
            Download invoice
          </Button>
        </PageSection>
      }
    />
  );
};

export function PurchaseDetails({ orderId, setFull }: PurchaseDetailProps) {
  const { t } = useTranslation();
  const [isOpenTransactionDetails, setIsOpenTransactionDetails] =
    useState(false);

  useEffect(() => {
    setFull?.(isOpenTransactionDetails);
  }, [setFull, isOpenTransactionDetails]);

  const [getOrder, { data: orderDetails, isLoading: isOrderLoading }] =
    useLazyGetOrderQuery();

  const { data: QRCodeData, isLoading: isQRCodeDataLoading } =
    useGetQRCodeDataQuery(
      {
        esimId: orderDetails?.esimId || '',
      },
      { skip: !orderDetails?.esimId }
    );

  useEffect(() => {
    getOrder({ id: orderId });
  }, [orderId]);

  return (
    <PageWrapper loading={isOrderLoading || isQRCodeDataLoading}>
      <div className='space-y-6'>
        {orderDetails?.package && (
          <>
            <div className='mt-2 flex text-wl-on-surface justify-between items-center'>
              <div className='flex flex-wrap gap-1'>
                <h2 className='text-M-16'>
                  {t('pages.purchaseDetails.invoiceId')}:
                </h2>
                <h2>{orderDetails.code}</h2>
              </div>
              <p className='opacity-50 text-M-14'>
                {dayjs(orderDetails.createdAt).format('MM/DD/YYYY')}
              </p>
            </div>
            <PageSection className='bg-wl-background p-4'>
              <PackageCard
                item={orderDetails.package}
                country={{
                  name: orderDetails.country
                    ? orderDetails.country.name
                    : orderDetails.region.name,
                }}
                showFlag={false}
                className='!p-0 bg-transparent'
              />
              <Divider className='my-4 bg-wl-border' />
              <ItemRow
                label={t('pages.purchaseDetails.smdp')}
                value={
                  QRCodeData?.smdpAddress ? (
                    <div className='flex justify-between'>
                      <span className='break-all'>
                        {QRCodeData.smdpAddress}
                      </span>
                      <CopyButton
                        className='text-wl-on-surface'
                        text={QRCodeData.smdpAddress}
                        successMessage={t('notification.success.copied', {
                          name: t('pages.purchaseDetails.smdp'),
                        })}
                      />
                    </div>
                  ) : (
                    ''
                  )
                }
                className='flex !items-start flex-col mb-4'
                labelClassName='text-nowrap !text-M-16'
                valueClassName='w-full'
              />
              <ItemRow
                label={t('pages.purchaseDetails.iosActivationCode')}
                value={
                  QRCodeData?.activationCode ? (
                    <div className='flex justify-between'>
                      <span className='break-all'>
                        {QRCodeData.activationCode}
                      </span>
                      <CopyButton
                        className='text-wl-on-surface'
                        text={QRCodeData.activationCode}
                        successMessage={t('notification.success.copied', {
                          name: t('pages.purchaseDetails.iosActivationCode'),
                        })}
                      />
                    </div>
                  ) : (
                    ''
                  )
                }
                className='flex !items-start flex-col mb-4'
                labelClassName='text-nowrap !text-M-16'
                valueClassName='w-full'
              />
              <ItemRow
                label={t('pages.purchaseDetails.androidActivationCode')}
                value={
                  QRCodeData?.fullCode ? (
                    <div className='flex justify-between'>
                      <span className='break-all'>{QRCodeData.fullCode}</span>
                      <CopyButton
                        className='text-wl-on-surface'
                        text={QRCodeData.fullCode}
                        successMessage={t('notification.success.copied', {
                          name: t(
                            'pages.purchaseDetails.androidActivationCode'
                          ),
                        })}
                      />
                    </div>
                  ) : (
                    ''
                  )
                }
                className='flex !items-start flex-col mb-4'
                labelClassName='text-nowrap !text-M-16'
              />
              <ItemRow
                label={t('pages.purchaseDetails.iccid')}
                value={orderDetails?.iccid || ''}
                className='flex !items-start flex-col'
                labelClassName='text-nowrap !text-M-16'
              />
            </PageSection>
          </>
        )}

        <div className='bg-wl-background rounded-xl flex items-center justify-center p-4'>
          <QRCode esimId={orderDetails?.esimId} className='w-[100px]' />
        </div>

        <PageSection className='text-wl-on-background rounded-xl bg-wl-background p-4'>
          <div className='text-M-16 mb-4'>
            {t('pages.purchaseDetails.orderSummary')}
          </div>
          <PriceRow
            label={t('pages.purchaseDetails.esimPrice')}
            price={orderDetails?.package?.price}
            currency={orderDetails?.currency}
            className='text-R-16'
            priceClassName='text-M-20'
          />
          {(orderDetails?.discountAmount ?? 0) > 0 && (
            <PriceRow
              label={t('pages.purchaseDetails.discount')}
              price={-Number(orderDetails?.discountAmount)}
              currency={orderDetails?.currency}
              className='mt-2 text-R-16'
              priceClassName='text-M-20 text-discount'
            />
          )}
          {(orderDetails?.taxAmount ?? 0) > 0 && (
            <PriceRow
              label={t('pages.purchaseDetails.otherFee')}
              price={orderDetails?.taxAmount}
              currency={orderDetails?.currency}
              className='mt-2 text-R-16'
              priceClassName='text-M-20'
            />
          )}
          <Divider className='my-2 bg-wl-border' />
          <PriceRow
            label={t('pages.purchaseDetails.total')}
            price={orderDetails?.finalAmount}
            currency={orderDetails?.currency}
            className='text-R-16'
            priceClassName='text-B-20'
          />
        </PageSection>

        <div
          className='flex items-center cursor-pointer text-wl-on-surface'
          onClick={() => setIsOpenTransactionDetails(!isOpenTransactionDetails)}
        >
          {isOpenTransactionDetails ? (
            <ArrowDown3 className='fill-wl-on-surface' variant='Linear' />
          ) : (
            <ArrowRight3 className='fill-wl-on-surface' variant='Linear' />
          )}
          {t('pages.purchaseDetails.transactionDetails')}
        </div>

        {isOpenTransactionDetails && (
          <PageSection className='bg-wl-background text-wl-on-background rounded-xl p-4'>
            <p className='text-M-16 mb-4'>
              {t('pages.purchaseDetails.transactionDetails')}
            </p>
            <div className='space-y-2'>
              <ItemRow
                label={t('pages.purchaseDetails.type')}
                value={orderDetails?.package?.packageType}
                labelClassName='text-R-16'
                valueClassName='text-M-14'
              />

              <PriceRow
                label={t('pages.purchaseDetails.amount')}
                price={orderDetails?.finalAmount}
                currency={orderDetails?.currency}
                className='text-R-16'
                priceClassName='text-M-14'
              />
              <ItemRow
                label={t('pages.purchaseDetails.time')}
                value={dayjs(orderDetails?.createdAt).format(
                  'MM/DD/YYYY - HH:mm'
                )}
                labelClassName='text-R-16'
                valueClassName='text-M-14'
              />

              {orderDetails?.gateway && (
                <ItemRow
                  label={t('pages.purchaseDetails.provider')}
                  value={gatewayIcon[orderDetails.gateway]}
                  labelClassName='text-R-16'
                  valueClassName='text-M-14'
                />
              )}

              <ItemRow
                label={t('pages.purchaseDetails.status.title')}
                labelClassName='text-R-16'
                valueClassName='text-M-14 font-semibold'
                value={
                  {
                    [ORDER_STATUS.PENDING]: (
                      <div className='text-warning p-1 font-bold text-xs bg-[#03A3001A]'>
                        {t('pages.purchaseDetails.status.pending')}
                      </div>
                    ),
                    [ORDER_STATUS.PROCEED]: (
                      <div className='text-success p-1 font-bold text-xs bg-[#03A3001A] rounded-md'>
                        {t('pages.purchaseDetails.status.proceed')}
                      </div>
                    ),
                    [ORDER_STATUS.COMPLETED]: (
                      <div className='text-success p-1 font-bold text-xs bg-[#03A3001A] rounded-md'>
                        {t('pages.purchaseDetails.status.complete')}
                      </div>
                    ),
                    [ORDER_STATUS.FAILED]: (
                      <div className='text-error p-1'>
                        {t('pages.purchaseDetails.status.failed')}
                      </div>
                    ),
                  }[orderDetails?.status || ORDER_STATUS.PENDING]
                }
              />
            </div>
          </PageSection>
        )}
      </div>
    </PageWrapper>
  );
}
